import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlanUi, type PlanKey, type PackRow } from "@/hooks/usePlanUi";
import { usePlanUpgrade } from "@/components/plan/PlanUpgradeDialog";
import { getPlanSubscription, startQueryPackCheckout } from "@/lib/planBilling.functions";

const euros = (c: number) => (c / 100).toLocaleString("es-ES", { maximumFractionDigits: 2 });

/**
 * Cabecera del panel con la capa de planes nueva: plan, consultas, prueba,
 * "Sube a X" y, en Avanzado, consultas extra. Incluye el aviso al 80 % y el
 * modal al llegar al límite. Solo se monta si usePlanUi().active.
 */
export default function PlanHeaderStatus() {
  const ui = usePlanUi();
  const es = ui.es;
  const { requestUpgrade, dialog } = usePlanUpgrade();
  const getStatus = useServerFn(getPlanSubscription);
  const buyPack = useServerFn(startQueryPackCheckout);
  const [open, setOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const warnedRef = useRef<string | null>(null);
  const shownLimitRef = useRef<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: sub } = useQuery({
    queryKey: ["plan-subscription-ui"],
    queryFn: () => getStatus(),
    enabled: ui.active && !ui.previewOn,
    staleTime: 60_000,
  });
  const s = sub?.subscription;
  const trialDays = s?.status === "trialing" && s.trial_end ? Math.max(0, Math.ceil((s.trial_end * 1000 - Date.now()) / 86_400_000)) : null;

  const { quota } = ui;
  const period = `${ui.planKey}:${quota.resetsAt ?? ""}`;

  // Aviso al 80 % (una vez por periodo y plan) y modal al llegar al límite (una vez por sesión y periodo).
  useEffect(() => {
    if (!ui.active || quota.limit == null) return;
    if (quota.warning && warnedRef.current !== period) {
      warnedRef.current = period;
      toast(es ? `Te quedan ${quota.remaining} consultas del Agente Retail IA este mes.` : `You have ${quota.remaining} Retail AI Agent queries left this month.`);
    }
    // En vista previa de admin no se abre solo: se prueba desde el popover.
    if (quota.exhausted && !ui.previewOn && shownLimitRef.current !== period) {
      shownLimitRef.current = period;
      setLimitOpen(true);
    }
  }, [ui.active, ui.previewOn, quota.limit, quota.warning, quota.exhausted, quota.remaining, period, es]);

  if (!ui.active || !ui.plan) return null;

  const openPack = async (pack: PackRow) => {
    if (ui.previewOn) { toast(es ? "Vista previa: aquí el usuario iría al pago del pack." : "Preview: the user would go to the pack checkout here."); return; }
    const win = window.open("", "_blank");
    try { const r = await buyPack({ data: { tier_id: pack.id } }); if (win && r.url) win.location.href = r.url; }
    catch { win?.close(); toast.error(es ? "Los packs estarán disponibles muy pronto." : "Packs will be available very soon."); }
  };
  const ADS_KEY = "quota.publicaciones_instagram";
  const adsItem = ui.currentItems.find((i) => i.catalog_key === ADS_KEY);
  const adsLimit = adsItem && adsItem.value_type === "limit" ? adsItem.limit ?? 0 : 0;
  const resets = quota.resetsAt ? new Date(quota.resetsAt).toLocaleDateString(es ? "es-ES" : "en-GB", { day: "numeric", month: "long" }) : "";

  const chipLabel = `${ui.planName(ui.planKey)}${quota.limit != null ? ` · ${quota.remaining}` : ""}${trialDays != null ? ` · ${trialDays}d` : ""}`;
  const hoverOpen = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const hoverClose = () => { if (closeTimer.current) clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 200); };

  const pk = ui.planKey;
  const low = quota.limit != null && (quota.exhausted || quota.warning || (quota.remaining ?? 0) <= 1);
  const active = quota.used > 0;
  const note = (() => {
    if (quota.exhausted) return es
      ? "¡Has exprimido tu plan al máximo! Tus consultas vuelven pronto y, si te quedas con ganas, hay planes con más margen para seguir avanzando."
      : "You've squeezed every drop out of your plan! Your queries are back soon, and there's always room to grow if you want more.";
    if (low) return es
      ? "Se nota que le estás sacando partido. Guarda tus últimas consultas para lo importante; si te quedas corto, siempre puedes ampliar."
      : "You're clearly making the most of it. Save your last queries for what matters; if you need more, you can always expand.";
    if (pk === "lite") return active
      ? (es ? "Vas muy bien. ¿Sabías que también te podemos ayudar con subvenciones y a mejorar tu local?" : "You're doing great. Did you know we can also help with grants and improving your shop?")
      : (es ? "Empieza preguntando lo que más te preocupe de tu negocio. Y recuerda que también te ayudamos con subvenciones y con tu local." : "Start by asking about what worries you most. We can also help with grants and your shop.");
    if (pk === "basico") return es
      ? "Lo estás haciendo genial. Con hoja de ruta y anuncios con IA tu tienda podría ir aún más rápido, cuando te apetezca."
      : "You're doing great. With a roadmap and AI ads your shop could move even faster, whenever you like.";
    if (pk === "club_pro") return es
      ? "Lo estás haciendo genial. Aprovecha tus anuncios y, si quieres más, siempre puedes subir de plan."
      : "You're doing great. Make the most of your ads and, if you want more, you can always upgrade.";
    return es
      ? "Tienes todo el potencial del Agente Retail IA en tus manos. Exprime tus anuncios y los módulos premium: tu comercio lo va a notar."
      : "You have the full power of the Retail AI Agent. Make the most of your ads and premium modules: your shop will notice.";
  })();

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" onClick={(e) => e.preventDefault()} onMouseEnter={hoverOpen} onMouseLeave={hoverClose} className="relative h-9 rounded-full border-border bg-card px-3 text-xs font-bold shadow-none" aria-label={es ? "Tu plan" : "Your plan"}>
            {chipLabel}
            {(quota.warning || quota.exhausted) && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-destructive" aria-hidden="true" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" onMouseEnter={hoverOpen} onMouseLeave={hoverClose} onOpenAutoFocus={(e) => e.preventDefault()} className="w-80 rounded-2xl border-border bg-card p-5 shadow-lg">
          <p className="font-display text-base font-bold text-foreground">
            {es ? "Tu plan: " : "Your plan: "}{ui.planName(pk)}
            {ui.previewOn && <span className="ml-2 font-body text-[11px] font-semibold text-muted-foreground">{es ? "(vista previa)" : "(preview)"}</span>}
          </p>

          <div className="mt-4">
            <p className="font-body text-xs font-semibold text-muted-foreground">{es ? "Consultas" : "Queries"}</p>
            {quota.limit == null ? (
              <p className="mt-1 font-display text-2xl font-bold text-foreground">{es ? "Ilimitadas" : "Unlimited"}</p>
            ) : (
              <>
                <p className="mt-1 font-figures text-2xl font-bold tabular-nums text-foreground">
                  {quota.remaining} <span className="font-body text-sm font-semibold text-muted-foreground">{es ? "listas para preguntar" : "ready to use"}</span>
                </p>
                <p className="mt-2 font-body text-[11px] font-semibold text-muted-foreground">
                  {es ? "Cuota de tu plan este mes: " : "Your plan this month: "}
                  <span className="font-figures tabular-nums">{Math.min(quota.used, quota.limit)}</span>{es ? " de " : " of "}<span className="font-figures tabular-nums">{quota.limit}</span>{es ? " usadas" : " used"}
                </p>
                <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(0, 100 - quota.pct)}%` }} />
                </div>
                {resets && <p className="mt-1.5 font-body text-[11px] text-muted-foreground">{es ? `Se renuevan el ${resets}` : `They renew on ${resets}`}</p>}
                {quota.credits > 0 && (
                  <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3">
                    <p className="font-body text-xs font-semibold text-foreground">
                      +<span className="font-figures tabular-nums">{quota.credits}</span> {es ? "consultas extra en reserva" : "extra queries in reserve"}
                    </p>
                    <p className="mt-1 font-body text-[11px] leading-relaxed text-muted-foreground">
                      {es ? "No caducan. Se usan solo cuando agotas las de tu plan." : "They never expire. Used only after your plan's queries run out."}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mt-4 border-t border-border pt-4">
            <p className="font-body text-xs font-semibold text-muted-foreground">{es ? "Anuncios con IA" : "AI ads"}</p>
            {adsLimit > 0 ? (
              <>
                <p className="mt-1 font-figures text-2xl font-bold tabular-nums text-foreground">
                  {adsLimit} <span className="font-body text-sm font-semibold text-muted-foreground">{es ? "al mes" : "a month"}</span>
                </p>
                <p className="mt-1 font-body text-[11px] text-muted-foreground">{es ? "Incluidos en tu plan, listos para usar." : "Included in your plan, ready to use."}</p>
              </>
            ) : (
              <>
                <p className="mt-1 font-display text-lg font-bold text-foreground">{es ? "No incluidos" : "Not included"}</p>
                <p className="mt-1 font-body text-[11px] text-muted-foreground">{es ? "Llegan con planes superiores." : "Available on higher plans."}</p>
              </>
            )}
          </div>

          {trialDays != null && <p className="mt-3 font-body text-xs font-semibold text-foreground">{es ? `Prueba gratis: quedan ${trialDays} días` : `Free trial: ${trialDays} days left`}</p>}

          <div className="mt-4 rounded-xl border border-primary/40 bg-primary/15 p-3">
            <p className="font-body text-xs leading-relaxed text-foreground">{note}</p>
          </div>
          {ui.previewOn && quota.limit != null && (
            <button type="button" onClick={() => { setOpen(false); setLimitOpen(true); }} className="mt-3 min-h-11 w-full font-body text-xs font-semibold text-muted-foreground underline-offset-2 hover:underline">
              {es ? "Ver aviso de consultas agotadas" : "Preview the out-of-queries notice"}
            </button>
          )}
        </PopoverContent>
      </Popover>

      <QuotaLimitDialog open={limitOpen} onClose={() => setLimitOpen(false)} ui={ui} resets={resets} packs={ui.packs} onPack={openPack}
        onUpgrade={(pk) => { setLimitOpen(false); requestUpgrade(pk, "quota_modal"); }} />
      {dialog}
    </>
  );
}

/** Pack Temporada (el tercero) solo para Club Pro y Avanzado. */
const packsForPlan = (packs: PackRow[], pk: PlanKey | null) =>
  !pk || pk === "lite" ? [] : packs.filter((p) => (p.tier_order ?? 1) < 3 || pk === "club_pro" || pk === "completo");

function QuotaLimitDialog({ open, onClose, ui, resets, packs, onPack, onUpgrade }: {
  open: boolean; onClose: () => void; ui: ReturnType<typeof usePlanUi>; resets: string;
  packs: PackRow[]; onPack: (p: PackRow) => void; onUpgrade: (p: PlanKey) => void;
}) {
  const es = ui.es;
  const pk = ui.planKey;
  const rule = ui.rule;
  const target = (pk === "completo" ? null : (rule?.target_plan_key ?? ui.nextPlan?.plan_key ?? null)) as PlanKey | null;
  const limit = ui.quota.limit ?? 0;
  const gains = target ? ui.diff(pk, target).filter((g) => !g.startsWith(es ? "Agente Retail IA" : "Retail AI Agent")).slice(0, 4) : [];
  const nextQuota = target ? ui.diff(pk, target).find((g) => g.startsWith(es ? "Agente Retail IA" : "Retail AI Agent"))?.split(":").pop()?.trim() : null;
  const myPacks = packsForPlan(packs, pk);
  const planName = ui.planName(pk);
  const title = es ? "Has usado todas tus consultas del mes" : "You've used all your queries this month";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>
            {es
              ? <>Has completado las <span className="font-figures tabular-nums">{limit}</span> consultas del Agente Retail IA incluidas en tu plan {planName}. {resets && `Se renuevan el ${resets}.`}</>
              : <>You've used the <span className="font-figures tabular-nums">{limit}</span> Retail AI Agent queries included in your {planName} plan. {resets && `They renew on ${resets}.`}</>}
          </DialogDescription>
        </DialogHeader>

        {pk === "completo" && (
          <p className="font-body text-sm text-foreground">
            {es ? "¡Has exprimido el plan más completo al máximo! Si no quieres esperar, recarga consultas: no caducan a fin de mes." : "You've squeezed the most complete plan to the max! If you don't want to wait, top up: extra queries don't expire at month end."}
          </p>
        )}

        {target && (
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="font-display text-sm font-bold text-foreground">
              {es ? `Con ${ui.planName(target)} tendrías` : `With ${ui.planName(target)} you'd get`}
            </p>
            {nextQuota && (
              <p className="mt-1 font-body text-sm text-foreground">
                <span className="font-figures tabular-nums">{limit}</span> → <span className="font-figures font-bold tabular-nums">{nextQuota}</span> {es ? "consultas al mes" : "queries a month"}
              </p>
            )}
            {gains.length > 0 && (
              <ul className="mt-2 space-y-1">{gains.map((g) => <li key={g} className="font-body text-sm text-muted-foreground">+ {g}</li>)}</ul>
            )}
            <Button className="mt-3 min-h-11 w-full rounded-full font-bold" onClick={() => onUpgrade(target)}>
              {pk === "lite"
                ? (es ? `Prueba ${ui.planName(target)} 7 días gratis` : `Try ${ui.planName(target)} free for 7 days`)
                : (es ? `Sube a ${ui.planName(target)}` : `Upgrade to ${ui.planName(target)}`)}
            </Button>
          </div>
        )}

        {myPacks.length > 0 && (
          <div>
            <p className="font-body text-xs font-semibold text-muted-foreground">
              {target ? (es ? "O recarga sin cambiar de plan" : "Or top up without changing plan") : (es ? "Recarga consultas" : "Top up queries")}
            </p>
            <div className="mt-2 space-y-2">
              {myPacks.map((p) => (
                <button key={p.id} type="button" onClick={() => onPack(p)}
                  className="flex min-h-11 w-full items-center justify-between rounded-xl border border-border bg-card px-4 py-2 text-left transition-colors hover:bg-muted">
                  <span className="font-body text-sm font-semibold text-foreground">
                    {(es ? p.label : p.label_en || p.label) ?? ""} · <span className="font-figures tabular-nums">{p.quota_amount}</span> {es ? "consultas" : "queries"}
                  </span>
                  <span className="font-figures text-sm font-bold tabular-nums text-foreground">{euros(p.price_cents ?? 0)} €</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <Button variant="outline" className="min-h-11 w-full rounded-full" onClick={onClose}>
          {es ? `Seguir en ${planName}` : `Stay on ${planName}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
